import { useAtom } from 'jotai';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { reservationDataAtom } from '../jotai-atoms/reservation-data.atom';
import { userAtom } from '../jotai-atoms/user.atom';
import { LiveClassShiftListItem, LiveClassShiftListItemProps } from './LiveClassShiftListItem';

export interface LiveClassShiftListProps {
  items: LiveClassShiftListItemProps[];
}

export const LiveClassShiftList: React.FC<LiveClassShiftListProps> = ({ items }) => {
  const __ = useTranslation().t;

  const [reservationData, setReservationData] = useAtom(reservationDataAtom);
  const [user] = useAtom(userAtom);

  const onClickItem = (item: LiveClassShiftListItemProps) => {
    if (item.type === 'private' && user?.subscriptionInfo.privateSubscriptionAvailable === 0) {
      setReservationData({
        ...reservationData,
        activeModal: 'modalReservationNoCredits',
      });
      return;
    } else if (
      (item.type === 'group' || item.type === 'gameclass') &&
      !user?.subscriptionInfo.hasSubscriptionTypeUnlimitedGroup &&
      user?.subscriptionInfo.groupSubscriptionAvailable === 0
    ) {
      setReservationData({
        ...reservationData,
        activeModal: 'modalReservationNoCredits',
      });
      return;
    }
    setReservationData({
      ...reservationData,
      activeModal: 'modalReservationReserve',
      selectedLiveClass: item,
    });
  };

  if (!items?.length) {
    return <NoClassesContainer>{__('noClassesFoundAtTimes')}</NoClassesContainer>;
  }

  const groupedItems = _.groupBy(items, 'dateTime');

  return (
    <Container>
      {Object.keys(groupedItems).map((dateTime, key) => {
        const groupTitle = moment(dateTime).format('h:mm a');
        return (
          <div key={key}>
            <StyledGroupTitle>{groupTitle}</StyledGroupTitle>
            {groupedItems[dateTime].map((liveClassShiftListItem, key) => (
              <StyledLiveClassShiftListItem
                {...liveClassShiftListItem}
                key={key}
                onClick={() => onClickItem(liveClassShiftListItem)}
              />
            ))}
          </div>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  margin: auto;
  max-height: 375px;
  max-width: 438px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 12px;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.xl}) {
    max-height: calc(100% - 175px);
  }

  /* Foreground, Background */
  scrollbar-color: rgba(0, 120, 255, 0.2) rgba(30, 134, 251, 0.05);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 120, 255, 0.2);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(30, 134, 251, 0.05);
    border-radius: 4px;
  }

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.md}) {
    max-width: 100%;
  }
`;

const StyledGroupTitle = styled.div`
  padding: 6px 12px;
  margin-bottom: 6px;
  margin-top: 2px;

  width: 100%;
  height: 28px;

  background: rgba(210, 231, 254, 0.32);
  border-radius: 2px;

  font-family: 'Baloo 2';
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;

  color: #000000;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.md}) {
    width: 100%;
  }
`;

const StyledLiveClassShiftListItem = styled(LiveClassShiftListItem)`
  margin-bottom: 6px;
`;

const NoClassesContainer = styled.div`
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  height: 130px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 75px;
  position: relative;
`;
